// components/NotificationDetails.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Image } from 'primereact/image';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/pt-br';
import NotificationService from '../../service/NotificationService';

moment.locale('pt-br');

const notificationService = new NotificationService();

const NotificationDetails = () => {
    const { id } = useParams();
    const navigate = useHistory();
    const toast = useRef(null);

    const [notification, setNotification] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);

    const iss = localStorage.getItem('iss');

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        page: 1
    });

    const [filters, setFilters] = useState({
        search: '',
        status: null
    });

    const statusOptions = [
        { label: 'Todos', value: null },
        { label: 'Lidas', value: 'read' },
        { label: 'Não Lidas', value: 'unread' }
    ];

    const loadNotificationDetails = useCallback(() => {
        setLoading(true);
        try {
            notificationService.getNotification(id, iss, lazyParams, filters).then(data => {
                setNotification(data.data.notification);
                setStatistics(data.data.statistics);
                setRecipients(data.data.recipients);
                setTotalRecords(data.data.pagination.total);

                setLoading(false);
            });

        } catch (error) {
            console.error('❌ Erro ao carregar detalhes:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro ao carregar detalhes da notificação',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    }, [filters, iss, lazyParams, id]);

    useEffect(() => {
        loadNotificationDetails();
    }, [lazyParams.page, lazyParams.rows, filters.status]);

    // Debounce para busca
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (lazyParams.page === 1) {
                loadNotificationDetails();
            } else {
                setLazyParams(prev => ({ ...prev, page: 1, first: 0 }));
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [filters.search]);

    const onPage = (event) => {
        const newPage = Math.floor(event.first / event.rows) + 1;
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: newPage
        });
    };

    // ==================== TEMPLATES ====================

    const userBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-3">
                {/* <Avatar
                    image={rowData.userAvatar}
                    icon={!rowData.userAvatar ? "pi pi-user" : null}
                    size="large"
                    shape="circle"
                    style={{ backgroundColor: '#dee2e6', color: '#495057' }}
                /> */}
                <div className='ml-2'>
                    <div className="font-semibold text-base">{rowData.userName}</div>
                    <div className="text-sm text-500">{rowData.userEmail}</div>
                </div>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        return (
            <Tag
                value={rowData.isRead ? 'Lida' : 'Não Lida'}
                severity={rowData.isRead ? 'success' : 'warning'}
                icon={rowData.isRead ? 'pi pi-check-circle' : 'pi pi-clock'}
                rounded
            />
        );
    };

    const readAtBodyTemplate = (rowData) => {
        if (!rowData.isRead) {
            return <span className="text-500 text-sm">Ainda não lida</span>;
        }

        return (
            <div>
                <div className="font-semibold">{moment(rowData.readAt).format('DD/MM/YYYY HH:mm')}</div>
                <div className="text-xs text-500">{moment(rowData.readAt).fromNow()}</div>
            </div>
        );
    };

    const deliveredAtBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="font-semibold">{rowData.deliveredAt != null ? moment(rowData.deliveredAt).format('DD/MM/YYYY HH:mm') : "sem data"}</div>
                <div className="text-xs text-500">{rowData.deliveredAt != null ? moment(rowData.deliveredAt).fromNow() : "sem data"}</div>
            </div>
        );
    };

    // ==================== HEADER DA TABELA ====================

    const tableHeader = (
        <div className="flex flex-wrap gap-3 align-items-center justify-content-between">
            <div className="flex gap-2">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        placeholder="Buscar por nome ou email..."
                        style={{ width: '300px' }}
                    />
                </span>

                <Dropdown
                    value={filters.status}
                    options={statusOptions}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.value }))}
                    placeholder="Filtrar por status"
                    style={{ width: '180px' }}
                />
            </div>

            <div className="text-500">
                <i className="pi pi-users mr-2"></i>
                {totalRecords} destinatários
            </div>
        </div>
    );

    // ==================== SKELETON LOADING ====================

    if (!notification && loading) {
        return (
            <div className="card">
                <Card>
                    <Skeleton width="100%" height="200px" className="mb-3" />
                    <Skeleton width="100%" height="400px" />
                </Card>
            </div>
        );
    }

    // ==================== RENDER ====================

    return (
        <div className="card">
            <Toast ref={toast} />

            {/* Botão Voltar */}
            <Button
                label="Voltar"
                icon="pi pi-arrow-left"
                className="p-button-text mb-3"
                onClick={() => navigate.push('/notificacoes/listar')}
            />

            {/* Card de Informações da Notificação */}
            <Card className="mb-4">
                <div className="grid">
                    {/* Coluna Esquerda - Imagem */}
                    <div className="col-12 md:col-4 lg:col-3">
                        {notification?.imageUrl ? (
                            <Image
                                src={notification.imageUrl}
                                alt={notification.title}
                                width="100%"
                                preview
                                imageStyle={{ borderRadius: '12px', objectFit: 'cover' }}
                            />
                        ) : (
                            <div
                                className="flex align-items-center justify-content-center"
                                style={{
                                    width: '100%',
                                    height: '200px',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '12px'
                                }}
                            >
                                <i className="pi pi-image" style={{ fontSize: '3rem', color: '#999' }}></i>
                            </div>
                        )}
                    </div>

                    {/* Coluna Direita - Informações */}
                    <div className="col-12 md:col-8 lg:col-9">
                        <div className="flex justify-content-between align-items-start mb-3">
                            <div>
                                <h2 className="mt-0 mb-2">{notification?.title}</h2>
                                <p className="text-600 text-lg mt-0">{notification?.body}</p>
                            </div>
                            <Tag
                                value={notification?.targetAudience === 'all' ? 'Todos' : 'Específico'}
                                severity={notification?.targetAudience === 'all' ? 'info' : 'warning'}
                                icon={notification?.targetAudience === 'all' ? 'pi pi-users' : 'pi pi-user'}
                            />
                        </div>

                        <Divider />

                        {/* Estatísticas */}
                        <div className="grid mt-3">
                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="text-center p-3 border-round" style={{ backgroundColor: '#f8f9fa' }}>
                                    <i className="pi pi-send text-4xl text-blue-500 mb-2"></i>
                                    <div className="text-2xl font-bold text-900 text-black-alpha-90">{statistics?.totalRecipients}</div>
                                    <div className="text-sm text-600">Total Enviadas</div>
                                </div>
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="text-center p-3 border-round" style={{ backgroundColor: '#f0fdf4' }}>
                                    <i className="pi pi-check-circle text-4xl text-green-500 mb-2"></i>
                                    <div className="text-2xl font-bold text-900 text-black-alpha-90">{statistics?.totalRead}</div>
                                    <div className="text-sm text-600">Lidas</div>
                                </div>
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="text-center p-3 border-round" style={{ backgroundColor: '#fef3c7' }}>
                                    <i className="pi pi-clock text-4xl text-orange-500 mb-2"></i>
                                    <div className="text-2xl font-bold text-900 text-black-alpha-90">{statistics?.totalUnread}</div>
                                    <div className="text-sm text-600">Não Lidas</div>
                                </div>
                            </div>

                            <div className="col-12 md:col-6 lg:col-3">
                                <div className="text-center p-3 border-round" style={{ backgroundColor: '#ede9fe' }}>
                                    <i className="pi pi-chart-line text-4xl text-purple-500 mb-2"></i>
                                    <div className="text-2xl font-bold text-900 text-black-alpha-90">{statistics?.readPercentage}%</div>
                                    <div className="text-sm text-600">Taxa de Leitura</div>
                                </div>
                            </div>
                        </div>

                        {/* Barra de Progresso */}
                        <div className="mt-4">
                            <div className="flex justify-content-between mb-2">
                                <span className="text-sm font-semibold">Progresso de Leitura</span>
                                <span className="text-sm text-500">
                                    {statistics?.totalRead} de {statistics?.totalRecipients} usuários
                                </span>
                            </div>
                            <ProgressBar
                                value={statistics?.readPercentage}
                                showValue={false}
                                style={{ height: '12px' }}
                                color={
                                    statistics?.readPercentage >= 70 ? '#22c55e' :
                                        statistics?.readPercentage >= 40 ? '#f59e0b' : '#ef4444'
                                }
                            />
                        </div>

                        {/* Data de Envio */}
                        <div className="mt-4 text-sm text-500">
                            <i className="pi pi-calendar mr-2"></i>
                            Enviada em {moment(notification?.sentAt).format('DD/MM/YYYY [às] HH:mm')}
                            <span className="ml-2">({moment(notification?.sentAt).fromNow()})</span>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Tabela de Destinatários */}
            <Card title="📋 Destinatários">
                <DataTable
                    value={recipients}
                    lazy
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows}
                    totalRecords={totalRecords}
                    onPage={onPage}
                    loading={loading}
                    header={tableHeader}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} destinatários"
                    emptyMessage="Nenhum destinatário encontrado"
                    responsiveLayout="scroll"
                    stripedRows
                    rowHover
                >
                    <Column
                        header="Usuário"
                        body={userBodyTemplate}
                        style={{ minWidth: '250px' }}
                    />

                    <Column
                        header="Status"
                        body={statusBodyTemplate}
                        style={{ width: '150px' }}
                    />

                    <Column
                        header="Lida em"
                        body={readAtBodyTemplate}
                        style={{ width: '200px' }}
                    />

                    <Column
                        header="Entregue em"
                        body={deliveredAtBodyTemplate}
                        style={{ width: '200px' }}
                    />
                </DataTable>
            </Card>
        </div>
    );
};

export default NotificationDetails;